import { useState } from 'react';
import { Form } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { useAtom } from 'jotai';

import Button from '@/components/Button/Button';
import InputText from '@/components/InputText/InputText';
import NotificationBanner from '@/components/NotificationBanner/NotificationBanner';
import { NotificationAtom } from '@/jotai/jotai';

type ResetPasswordRequestForm = {
  email: string;
};

type ResetPasswordForm = {
  code: string;
  password: string;
  passwordConfirm: string;
};

export const PasswordReminder = () => {
  const navigator = useNavigate();
  const [email, setEmail] = useState('');
  const [_, setNotification] = useAtom(NotificationAtom);
  const [isEmainSent, setIsEmailSent] = useState(false);

  const { register, handleSubmit } = useForm<ResetPasswordRequestForm>();
  const onSubmitUpdatePassword: SubmitHandler<ResetPasswordRequestForm> = async (data) => {
    try {
      const result = await resetPassword({ username: data.email });
      if (result.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        setEmail(data.email);
        setIsEmailSent(true);
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: '登録いただいたメールアドレスに再設定用のメールを送信しました',
          message: 'メールに記載された確認コードを入力し、新しいパスワードを設定してください',
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const { register: register2, handleSubmit: handleSubmit2 } = useForm<ResetPasswordForm>();
  const onSubmit: SubmitHandler<ResetPasswordForm> = async (data) => {
    try {
      if (data.password !== data.passwordConfirm) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'パスワードが一致しません',
          message: 'パスワードをもう一度入力してください',
        });
        throw new Error('パスワードが一致しません');
      }
      await confirmResetPassword({
        username: email,
        confirmationCode: data.code,
        newPassword: data.password,
      }).then(() => {
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: '新しいパスワードの設定が完了しました',
          message: '再度ログインをお願いいたします',
        });
        navigator('/admin/login');
      });
    } catch (e) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'パスワード再設定に失敗しました',
        message: '再度お試しください',
      });
    }
  };

  return (
    <>
      <NotificationBanner className="mx-auto w-1/2" />
      {!isEmainSent ? (
        <>
          <h1 className="mt-20 text-center text-2xl font-700 md:mb-10">
            放デイシステム（仮）にログイン
          </h1>
          <div className="mx-auto mb-10 max-w-[800px] rounded-3xl bg-white p-10">
            <h1 className="mb-10 text-center text-2xl font-700">パスワードを忘れた方</h1>
            <div className="m-auto flex w-[400px] flex-col items-center justify-center">
              <div className="w-full">
                <h2 className="mb-6 text-base">
                  アカウントに登録されているメールアドレスを入力してください。再設定メールを送信します。
                </h2>
                <div className="space-y-8">
                  <Form
                    className="md:w-[400px]"
                    onSubmit={handleSubmit(onSubmitUpdatePassword)}
                  >
                    <InputText
                      name="メールアドレス"
                      label="メールアドレス"
                      type="email"
                      supportText="メールアドレスを入力してください"
                      required
                      register={register('email')}
                    />
                    <div className="mt-8">
                      <Button
                        className="w-full"
                        variant="solid-fill"
                        size="lg"
                        type="submit"
                      >
                        パスワード再設定メールを送信
                      </Button>
                    </div>
                  </Form>
                  <div className="text-center">
                    <a
                      className="text-blue-800 underline"
                      href="/admin/login"
                    >
                      ログイン画面へ
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <h1 className="mt-20 text-center text-2xl font-700 md:mb-10">新しいパスワードを入力</h1>
          <div className="mx-auto mb-10 max-w-[800px] rounded-3xl bg-white p-10">
            <div className="justify-centerpb-4 m-auto flex flex-col items-center rounded-3xl bg-white md:w-full">
              <div className="space-y-8 p-8">
                <Form
                  className="md:w-[400px]"
                  onSubmit={handleSubmit2(onSubmit)}
                >
                  <InputText
                    name="code"
                    type="text"
                    label="認証コード"
                    supportText="メールアドレスに送信された認証コードを入力してください"
                    required
                    register={register2('code')}
                  />
                  <InputText
                    name="password"
                    type="password"
                    label="新しいパスワード"
                    supportText="半数字記号10文字以上"
                    required
                    register={register2('password')}
                  />
                  <InputText
                    name="password"
                    type="password"
                    label="新しいパスワード（確認用）"
                    supportText="半数字記号10文字以上"
                    required
                    register={register2('passwordConfirm')}
                  />
                  <div className="mt-8">
                    <Button
                      className="w-full"
                      variant="solid-fill"
                      size="lg"
                      type="submit"
                    >
                      パスワード再設定
                    </Button>
                  </div>
                </Form>
                <div className="text-center">
                  <a
                    className="text-blue-800 underline"
                    href="/password-reminder"
                  >
                    メール送信画面に戻る
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PasswordReminder;
